import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import { useToast } from '@/components/feedback/toast';
import { movePoints, popSnapshot, pushSnapshot } from '@/lib/maps/field-drawing';
import { isStrictlyInsideRings } from '@/lib/maps/hit-test';
import { beginPointerDrag, PLACEMENT_HOLD_MS } from '@/lib/maps/pointer-drag';
import type { MapPoint, MapProjection } from '@/lib/maps/types';

import { checkShapePlacement, refuseVerdict, type PlacementCheck } from './check-placement';
import { useValidateBoundary } from './hooks';
import { IMPORT_TOAST_KEY, IMPORT_TOAST_SECONDS, type PlacementVerdict } from './schemas';

/**
 * One position the shape has actually been committed to, **with the server's answer for it**.
 *
 * The two travel together rather than side by side because the move history steps through
 * positions and their verdicts must step with them: a shape imported outside the USA, dragged
 * somewhere valid, then Undone, is back on a position the server refused — and Place Field has to
 * go back to disabled. One `pop()` restores both, which is the same argument ADR-0018 makes for the
 * editor's own snapshot stack.
 *
 * `verdict: null` is **not evaluated** — the state between entering Placement mode with a `null`
 * advisory verdict and the answering round trip landing. It is never "valid": `canPlace` reads
 * `verdict?.ok === true`, so the un-evaluated case and the refused case are disabled by the same
 * expression.
 */
type PlacedAnchor = {
  points: readonly MapPoint[];
  verdict: PlacementCheck | null;
};

export type ShapePlacement = {
  /** Whether the Grower is in **Placement mode**. False until {@link ShapePlacement.begin}. */
  isPlacing: boolean;
  /** Enter Placement mode with the chosen shape's **Point**s and the import response's **advisory**
   *  verdict for it. The one caller is Continue. Also the reason the move history is empty on
   *  arrival: it is emptied here rather than remembered. */
  begin: (points: readonly MapPoint[], advisory: PlacementVerdict | null) => void;
  /** What the canvas draws: the live drag position while a gesture is in flight or its verdict is
   *  in flight, the committed position otherwise. One value, so nothing downstream has to know. */
  points: readonly MapPoint[];
  /** A gesture is moving the shape RIGHT NOW — the map's own gestures are suppressed for its
   *  length, and come back the instant the pointer lifts. */
  isDragging: boolean;
  /** **Place Field is enabled ⟺ the current anchor's verdict is valid.** `false` while a verdict is
   *  in flight and `false` for an un-evaluated advisory verdict, both by the same `?.ok === true`:
   *  the server's `placement: null` means NOT EVALUATED and is never read as valid. */
  canPlace: boolean;
  /** `stack.length > 0` — the **level 0** the shipped Undo control reads. */
  canUndo: boolean;
  undo: () => void;
  /** Called from the map AREA's own `pointerdown`. Answers whether the press landed inside the
   *  shape and, if it did, runs the held-drag gesture — unless a release's verdict is still in
   *  flight, in which case the shape is not grabbable at all and the press pans the map. */
  onPointerDown: (
    event: ReactPointerEvent<HTMLElement>,
    context: { projection: MapProjection | null; rect: DOMRect | undefined },
  ) => void;
};

/**
 * **Placement mode** (113609 "Placing the Shape", ADO 119741).
 *
 * The shape is held as `MapPoint`s and moved with {@link movePoints}, and everything that follows
 * from that is an acceptance criterion satisfied **by construction** rather than by code:
 *
 * - Panning and zooming leave it over the same ground, because the map re-projects lat/lng and
 *   nothing here recomputes anything.
 * - It cannot be resized, because no scale factor exists anywhere in the path — and therefore
 *   there is no handle to render and nothing to disable.
 *
 * **The move history is this hook's own `pushSnapshot` stack**, `T = readonly MapPoint[]`. It is a
 * separate instance from Field Mapping mode's and dies when the import page unmounts, so "the move
 * history from placement mode is not carried through" (113609, ticket 07) needs no clearing step —
 * it is structural rather than remembered.
 *
 * A hook in `features/fields` rather than state in the page for the reason grilling Q2 imposed on
 * this whole surface: `<MapPage>` reached 933 lines and this page must not follow it.
 */
export function useShapePlacement(): ShapePlacement {
  // `null` IS preview mode. Folding the mode into the geometry means the two can never disagree —
  // there is no reachable state where the page believes it is placing and the hook holds no shape.
  const [anchor, setAnchor] = useState<PlacedAnchor | null>(null);
  // The live position while a gesture is in flight, AND across the verdict round trip that follows
  // it: the shape stays where it was dropped until the server answers, rather than snapping back
  // and forward. Cleared on both outcomes.
  const [preview, setPreview] = useState<readonly MapPoint[] | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [history, setHistory] = useState<readonly PlacedAnchor[]>([]);
  const { mutateAsync } = useValidateBoundary();
  const toast = useToast();
  /** Which release is the current one. A second drag begun while the first release is still in
   *  flight must win: without this, the older reply lands last and commits a position the Grower has
   *  already moved on from. */
  const release = useRef(0);
  /** A release whose verdict has not landed yet. The shape is drawn where it was dropped and is
   *  deliberately NOT grabbable until the server answers: a gesture begun now would hit-test
   *  against the committed ring while the Grower sees the dropped one, and would translate from a
   *  stale origin. Reading the transient position instead was rejected — a refusal landing
   *  mid-gesture would then yank the shape out from under the pointer. */
  const awaitingVerdict = useRef(false);

  // Invalidate every in-flight reply when the page unmounts. Without this a Grower who cancels out
  // of the import mid-round-trip is shown a placement refusal on the Map, about a shape they left.
  // Bumping the same counter the concurrency guard reads means there is one rule, not two.
  useEffect(
    () => () => {
      release.current += 1;
    },
    [],
  );

  /**
   * One place for every placement refusal, so a refused release and a refused entry are told the
   * same way — the shipped dark toast, `toneFor`'s tone (red `XCircle` for the USA rule per Figma
   * `783:667876`, amber `Warning` for everything else per `783:667352`), in the import flow's own
   * 5 s keyed slot. The key is what makes a repeated refusal restart one timer instead of stacking
   * a second toast.
   */
  const settle = useCallback(
    (next: PlacementCheck) => {
      if (!next.ok) {
        toast[next.tone]({
          title: next.message,
          key: IMPORT_TOAST_KEY,
          duration: IMPORT_TOAST_SECONDS,
        });
      }
      return next;
    },
    [toast],
  );

  const begin = useCallback(
    (points: readonly MapPoint[], advisory: PlacementVerdict | null) => {
      // A non-null advisory verdict is the server's own answer for the shape's imported position
      // and is taken as-is — no round trip. `null` means the server did NOT evaluate it (it caps
      // advisory containment at 10 selectable shapes) and must never be read as valid, so it opens
      // un-evaluated and one `validate-boundary` answers it.
      const verdict = advisory
        ? settle(advisory.valid ? { ok: true } : refuseVerdict(advisory))
        : null;
      setAnchor({ points, verdict });
      setPreview(null);
      // Undo starts disabled because the stack starts empty — the AC, stated rather than remembered.
      setHistory([]);
      // Bumped on BOTH paths, before the advisory short-circuit: re-entering Placement mode is a
      // new position whatever answered it, and an older reply still in flight must not land on it.
      const ticket = ++release.current;
      if (advisory) return;

      void checkShapePlacement(mutateAsync, points).then((next) => {
        if (ticket !== release.current) return;
        settle(next);
        // Functional, and keyed on the POINTS rather than on identity: the Grower may already have
        // dragged the shape while this was in flight, and this reply is about the position it
        // opened at, not the one it now sits on.
        setAnchor((current) =>
          current && current.points === points ? { ...current, verdict: next } : current,
        );
      });
    },
    [mutateAsync, settle],
  );

  // Reads `history` and sets both pieces of state side by side, rather than reaching for
  // `setAnchor` from inside a `setHistory` updater. An updater must be a PURE function of the
  // previous state — React 19's StrictMode deliberately invokes it twice in dev to surface exactly
  // that impurity — and while the doubled `setAnchor` happens to be idempotent here (both
  // invocations see the same stack), it is one concurrent `setHistory` away from silently
  // restoring the wrong snapshot. `history` in the deps is harmless: unlike `onProjectionChanged`,
  // nothing downstream of `onUndo` is torn down by an identity change.
  const undo = useCallback(() => {
    const popped = popSnapshot(history);
    if (!popped) return;
    setHistory(popped.rest);
    // The snapshot carries its own verdict, so stepping back onto the imported position of a
    // mis-projected file disables Place Field again with no separate bookkeeping.
    setAnchor(popped.snapshot);
  }, [history]);

  const onPointerDown = useCallback(
    (
      event: ReactPointerEvent<HTMLElement>,
      { projection, rect }: { projection: MapProjection | null; rect: DOMRect | undefined },
    ) => {
      if (!anchor || awaitingVerdict.current || !projection || !rect) return;
      const toPoint = ({ x, y }: { x: number; y: number }) =>
        projection.toPoint({ x: x - rect.left, y: y - rect.top });

      const grabbed = toPoint({ x: event.clientX, y: event.clientY });
      if (!grabbed) return;

      // "A press and drag starting anywhere OUTSIDE the shape pans the map and does not move the
      // shape." Returning here registers no gesture at all, so Google keeps the press and pans —
      // the absence of code IS the behaviour.
      //
      // `isStrictlyInsideRings` in the `{ x: lon, y: lat }` geographic plane at zero tolerance, the
      // same transposition `check-placement.ts` already makes. Reused verbatim rather than
      // re-derived: loosening this test is how a client becomes stricter than its own server, the
      // live defect ADR-0018 records in the admin twin and instructs us not to reproduce.
      const ring = anchor.points.map((point) => ({ x: point.lon, y: point.lat }));
      if (!isStrictlyInsideRings({ x: grabbed.lon, y: grabbed.lat }, [ring])) return;

      // Translate from the ORIGINAL points on every frame, never from the previous one, so a long
      // drag cannot accumulate rounding. The WHOLE anchor is captured, not just its points: if the
      // release is refused it is this anchor — verdict included — that the shape returns to.
      const from = anchor;
      const origin = from.points;

      beginPointerDrag(event, {
        toPoint,
        // The hold. A threshold crossing BEFORE it elapses voids the gesture permanently —
        // `beginPointerDrag` reports nothing and ends `(false, null)` — so a fast drag from inside
        // the shape is a map pan, exactly as grilling Q15 settled. NOT to be reconciled with
        // `DOUBLE_CLICK_WINDOW_MS` (250 ms, marking a Point): different gestures, different
        // stories, nearby numbers.
        holdMs: PLACEMENT_HOLD_MS,
        onMove: (point) => {
          // Set on the FIRST reported move, which is the first threshold crossing after the hold.
          // Google may pan by up to the 3px threshold before this lands; below that distance its
          // own drag has not visibly started either, so nothing is observable.
          setIsDragging(true);
          setPreview(movePoints(origin, grabbed, point));
        },
        onEnd: (dragged, last) => {
          // Gestures come back the instant the pointer lifts, not when anything else settles.
          setIsDragging(false);
          if (!dragged || !last) {
            setPreview(null);
            return;
          }
          const moved = movePoints(origin, grabbed, last);
          // The shape STAYS where it was dropped while the server answers. Snapping it back to the
          // committed position and forward again on acceptance would read as a glitch on every
          // valid drag, which is the common case.
          setPreview(moved);
          awaitingVerdict.current = true;

          const ticket = ++release.current;
          void checkShapePlacement(mutateAsync, moved).then((next) => {
            // Released FIRST, before the ticket check, so a superseded reply still unlocks the
            // shape — otherwise a stale answer would leave it ungrabbable for good.
            awaitingVerdict.current = false;
            if (ticket !== release.current) return;
            settle(next);
            // A refused release is NOT A MOVE: the transient position is discarded, nothing is
            // pushed, and the shape is back at `from` — which IS the last valid position, so no
            // `lastValidAnchor` variable exists and Undo never steps through a position the server
            // refused.
            if (!next.ok) {
              setPreview(null);
              return;
            }
            setHistory((stack) => pushSnapshot(stack, from));
            setAnchor({ points: moved, verdict: next });
            setPreview(null);
          });
        },
      });
    },
    [anchor, mutateAsync, settle],
  );

  return {
    isPlacing: anchor !== null,
    begin,
    points: preview ?? anchor?.points ?? [],
    isDragging,
    canPlace: anchor?.verdict?.ok === true,
    canUndo: history.length > 0,
    undo,
    onPointerDown,
  };
}
